// =============================================================================
// Export Utilities for Analytics and Reporting
// =============================================================================

import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { saveAs } from 'file-saver';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import { format } from 'date-fns';
import { LEGAL_ENTITY, FONT_SIZES } from '../components/documents/documentConfig';

/**
 * PDF Export Utilities
 */
export class PDFExporter {
  constructor() {
    this.pdf = null;
    this.pageHeight = 297; // A4 height in mm
    this.pageWidth = 210; // A4 width in mm
    this.margin = 20;
    this.yPosition = this.margin;
  }

  /**
   * Create new PDF document
   */
  createDocument(title = 'Eqaya Report') {
    this.pdf = new jsPDF();
    this.yPosition = this.margin;
    
    // Add header
    this.addHeader(title);
    
    return this;
  }

  /**
   * Add header to PDF
   */
  addHeader(title) {
    // Logo/Company name
    this.pdf.setFontSize(FONT_SIZES.pdf.coverTitle);
    this.pdf.setFont('helvetica', 'bold');
    this.pdf.text(LEGAL_ENTITY, this.margin, this.yPosition);

    this.yPosition += 10;

    // Report title
    this.pdf.setFontSize(FONT_SIZES.pdf.documentTitle);
    this.pdf.setFont('helvetica', 'normal');
    this.pdf.text(title, this.margin, this.yPosition);
    
    this.yPosition += 10;
    
    // Date
    this.pdf.setFontSize(12);
    this.pdf.text(`Generated: ${format(new Date(), 'PPP')}`, this.margin, this.yPosition);
    
    this.yPosition += 15;
    
    // Line separator
    this.pdf.line(this.margin, this.yPosition, this.pageWidth - this.margin, this.yPosition);
    this.yPosition += 10;
    
    return this;
  }

  /**
   * Add section header
   */
  addSection(title) {
    this.checkPageBreak(20);
    
    this.pdf.setFontSize(14);
    this.pdf.setFont('helvetica', 'bold');
    this.pdf.text(title, this.margin, this.yPosition);
    
    this.yPosition += 10;
    
    return this;
  }

  /**
   * Add text content
   */
  addText(text, fontSize = 12) {
    this.checkPageBreak(10);
    
    this.pdf.setFontSize(fontSize);
    this.pdf.setFont('helvetica', 'normal');
    
    // Handle multi-line text
    const lines = this.pdf.splitTextToSize(text, this.pageWidth - (2 * this.margin));
    this.pdf.text(lines, this.margin, this.yPosition);
    
    this.yPosition += lines.length * 5;
    
    return this;
  }

  /**
   * Add table data
   */
  addTable(headers, data) {
    this.checkPageBreak(30);
    
    const startY = this.yPosition;
    const rowHeight = 8;
    const colWidth = (this.pageWidth - (2 * this.margin)) / headers.length;
    
    // Draw headers
    this.pdf.setFontSize(10);
    this.pdf.setFont('helvetica', 'bold');
    
    headers.forEach((header, index) => {
      const x = this.margin + (index * colWidth);
      this.pdf.rect(x, startY, colWidth, rowHeight);
      this.pdf.text(header, x + 2, startY + 5);
    });
    
    this.yPosition += rowHeight;
    
    // Draw data rows
    this.pdf.setFont('helvetica', 'normal');
    
    data.forEach((row, rowIndex) => {
      this.checkPageBreak(rowHeight + 5);
      
      Object.values(row).forEach((value, colIndex) => {
        const x = this.margin + (colIndex * colWidth);
        this.pdf.rect(x, this.yPosition, colWidth, rowHeight);
        
        // Truncate long text
        const text = String(value).substring(0, 20);
        this.pdf.text(text, x + 2, this.yPosition + 5);
      });
      
      this.yPosition += rowHeight;
    });
    
    this.yPosition += 5;
    
    return this;
  }

  /**
   * Add chart as image
   */
  async addChart(chartElement, title = '') {
    try {
      this.checkPageBreak(100);
      
      if (title) {
        this.addSection(title);
      }
      
      // Convert chart to canvas
      const canvas = await html2canvas(chartElement, {
        backgroundColor: '#ffffff',
        scale: 2
      });
      
      // Convert canvas to image data
      const imgData = canvas.toDataURL('image/png');
      
      // Calculate dimensions
      const imgWidth = this.pageWidth - (2 * this.margin);
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      // Add image to PDF
      this.pdf.addImage(imgData, 'PNG', this.margin, this.yPosition, imgWidth, imgHeight);
      
      this.yPosition += imgHeight + 10;
      
    } catch (error) {
      console.error('Error adding chart to PDF:', error);
      this.addText('Chart could not be exported');
    }
    
    return this;
  }

  /**
   * Add key metrics summary
   */
  addMetricsSummary(metrics) {
    this.addSection('Key Metrics Summary');
    
    Object.entries(metrics).forEach(([key, value]) => {
      this.checkPageBreak(8);
      
      this.pdf.setFontSize(12);
      this.pdf.setFont('helvetica', 'bold');
      this.pdf.text(`${key}:`, this.margin, this.yPosition);
      
      this.pdf.setFont('helvetica', 'normal');
      const valueText = typeof value === 'number' ? value.toLocaleString() : String(value);
      this.pdf.text(valueText, this.margin + 80, this.yPosition);
      
      this.yPosition += 8;
    });
    
    this.yPosition += 5;
    
    return this;
  }

  /**
   * Check if page break is needed
   */
  checkPageBreak(requiredSpace) {
    if (this.yPosition + requiredSpace > this.pageHeight - this.margin) {
      this.pdf.addPage();
      this.yPosition = this.margin;
    }
  }

  /**
   * Save PDF file
   */
  save(filename = `buildzim-report-${format(new Date(), 'yyyy-MM-dd')}.pdf`) {
    if (!this.pdf) {
      throw new Error('No PDF document created. Call createDocument() first.');
    }
    
    this.pdf.save(filename);
  }

  /**
   * Get PDF as blob for upload
   */
  getBlob() {
    if (!this.pdf) {
      throw new Error('No PDF document created. Call createDocument() first.');
    }
    
    return this.pdf.output('blob');
  }
}

/**
 * CSV Export Utilities
 */
export class CSVExporter {
  /**
   * Export data to CSV
   */
  static exportToCSV(data, filename = `export-${format(new Date(), 'yyyy-MM-dd')}.csv`) {
    const csv = Papa.unparse(data, {
      header: true,
      skipEmptyLines: true
    });
    
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, filename);
  }

  /**
   * Export with custom formatting
   */
  static exportFormattedCSV(data, options = {}) {
    const {
      filename = `export-${format(new Date(), 'yyyy-MM-dd')}.csv`,
      delimiter = ',',
      headers = null,
      transform = null
    } = options;

    let processedData = data;
    
    // Apply data transformation if provided
    if (transform && typeof transform === 'function') {
      processedData = data.map(transform);
    }
    
    const config = {
      header: true,
      delimiter,
      skipEmptyLines: true
    };
    
    // Use custom headers if provided
    if (headers && Array.isArray(headers)) {
      config.columns = headers;
    }
    
    const csv = Papa.unparse(processedData, config);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, filename);
  }
}

/**
 * Excel Export Utilities
 */
export class ExcelExporter {
  /**
   * Export data to Excel
   */
  static exportToExcel(data, filename = `export-${format(new Date(), 'yyyy-MM-dd')}.xlsx`) {
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    
    XLSX.utils.book_append_sheet(wb, ws, 'Data');
    
    // Auto-size columns
    const colWidths = [];
    const range = XLSX.utils.decode_range(ws['!ref']);
    
    for (let col = range.s.c; col <= range.e.c; col++) {
      let maxWidth = 10;
      
      for (let row = range.s.r; row <= range.e.r; row++) {
        const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
        const cell = ws[cellAddress];
        
        if (cell && cell.v) {
          const cellLength = String(cell.v).length;
          maxWidth = Math.max(maxWidth, cellLength);
        }
      }
      
      colWidths.push({ width: Math.min(50, maxWidth + 2) });
    }
    
    ws['!cols'] = colWidths;
    
    XLSX.writeFile(wb, filename);
  }

  /**
   * Export multiple sheets
   */
  static exportMultipleSheets(sheets, filename = `report-${format(new Date(), 'yyyy-MM-dd')}.xlsx`) {
    const wb = XLSX.utils.book_new();
    
    Object.entries(sheets).forEach(([sheetName, data]) => {
      const ws = XLSX.utils.json_to_sheet(data);
      XLSX.utils.book_append_sheet(wb, ws, sheetName);
    });
    
    XLSX.writeFile(wb, filename);
  }
}

/**
 * Comprehensive Report Generator
 */
export class ReportGenerator {
  constructor(title, data) {
    this.title = title;
    this.data = data;
    this.metrics = {};
    this.charts = [];
    this.tables = [];
  }

  /**
   * Add metrics to report
   */
  addMetrics(metrics) {
    this.metrics = { ...this.metrics, ...metrics };
    return this;
  }

  /**
   * Add chart element to report
   */
  addChart(element, title) {
    this.charts.push({ element, title });
    return this;
  }

  /**
   * Add table data to report
   */
  addTable(headers, data, title) {
    this.tables.push({ headers, data, title });
    return this;
  }

  /**
   * Generate comprehensive PDF report
   */
  async generatePDF(filename) {
    const exporter = new PDFExporter();
    exporter.createDocument(this.title);
    
    // Add metrics summary
    if (Object.keys(this.metrics).length > 0) {
      exporter.addMetricsSummary(this.metrics);
    }
    
    // Add charts
    for (const chart of this.charts) {
      await exporter.addChart(chart.element, chart.title);
    }
    
    // Add tables
    this.tables.forEach(table => {
      if (table.title) {
        exporter.addSection(table.title);
      }
      exporter.addTable(table.headers, table.data);
    });
    
    exporter.save(filename);
  }

  /**
   * Generate Excel report
   */
  generateExcel(filename) {
    const sheets = {};
    
    // Add raw data sheet
    if (this.data && Array.isArray(this.data)) {
      sheets['Raw Data'] = this.data;
    }
    
    // Add metrics sheet
    if (Object.keys(this.metrics).length > 0) {
      sheets['Metrics'] = Object.entries(this.metrics).map(([key, value]) => ({
        Metric: key,
        Value: value
      }));
    }
    
    // Add table sheets
    this.tables.forEach((table, index) => {
      const sheetName = table.title || `Table ${index + 1}`;
      sheets[sheetName] = table.data;
    });
    
    ExcelExporter.exportMultipleSheets(sheets, filename);
  }

  /**
   * Generate CSV report (raw data only)
   */
  generateCSV(filename) {
    if (this.data && Array.isArray(this.data)) {
      CSVExporter.exportToCSV(this.data, filename);
    } else {
      console.warn('No data available for CSV export');
    }
  }
}

/**
 * Quick export functions for common use cases
 */
export const quickExports = {
  /**
   * Export dashboard data to PDF
   */
  async dashboardToPDF(dashboardElement, title, metrics = {}) {
    const exporter = new PDFExporter();
    exporter.createDocument(title);
    
    if (Object.keys(metrics).length > 0) {
      exporter.addMetricsSummary(metrics);
    }
    
    await exporter.addChart(dashboardElement, 'Dashboard Overview');
    exporter.save();
  },

  /**
   * Export table data to Excel
   */
  tableToExcel(data, filename = 'table-export.xlsx') {
    ExcelExporter.exportToExcel(data, filename);
  },

  /**
   * Export analytics data to CSV
   */
  analyticsToCSV(data, filename = 'analytics-data.csv') {
    CSVExporter.exportToCSV(data, filename);
  }
};

export default {
  PDFExporter,
  CSVExporter,
  ExcelExporter,
  ReportGenerator,
  quickExports
};